<template>
    <div class="announcement-audit">
        <!-- 页面标题 -->
        <div class="page-header">
            <h2 class="page-title">招标公告审核</h2>
        </div>
        <!-- 搜索区域 -->
        <div class="search-section">
            <div class="search-form">
                <label class="search-label">公告名称:</label>
                <el-input v-model="searchForm.announcementName" placeholder="请输入公告名称" class="search-input" clearable />
                <el-button type="primary" icon="el-icon-search" @click="handleSearch" class="search-btn">
                    搜索
                </el-button>
            </div>
            <!-- 审核状态切换按钮 -->
            <div class="audit-status-tabs">
                <div class="tab-buttons">
                    <button 
                        class="tab-button" 
                        :class="{ active: currentAuditStatus === 'unaudited' }"
                        @click="switchAuditStatus('unaudited')"
                    >
                        未审核公告
                    </button>
                    <button 
                        class="tab-button" 
                        :class="{ active: currentAuditStatus === 'audited' }"
                        @click="switchAuditStatus('audited')"
                    >
                        已审核公告
                    </button>
                </div>
            </div>
        </div>

        <!-- 数据表格 -->
        <div class="table-section">
            <el-table :data="tableData" v-loading="loading" border style="width: 100%" class="data-table">
                <el-table-column prop="no" label="NO." width="60" align="center" >
                    <template slot-scope="scope">
                        {{ scope.$index + 1 }}
                    </template>
                </el-table-column>
                <el-table-column prop="tenderNoticeName" label="公告名称"  show-overflow-tooltip />
                <el-table-column prop="tenderNoticeCode" label="公告编号" align="center" show-overflow-tooltip />
                <el-table-column prop="noticeNature" label="公告性质" align="center">
                    <template slot-scope="scope">
                        {{ noticeNatureDict[scope.row.noticeNature] || '-' }}
                    </template>
                </el-table-column>
                <el-table-column prop="noticeType" label="公告类型"  align="center">
                    <template slot-scope="scope">
                        {{ noticeTypeDict[scope.row.noticeType] || '-' }}
                    </template>
                </el-table-column>
                <el-table-column prop="createTime" label="创建时间"  align="center" />
                <el-table-column prop="noticeSendTime" label="公告发布时间" align="center" />
                <el-table-column prop="createBy" label="创建人"  align="center" />
                <el-table-column prop="auditTime" label="审核时间"  align="center" />
                <el-table-column prop="status" label="状态" width="120" align="center">
                    <template slot-scope="scope">
                        {{ statusDict[scope.row.status] || '-' }}
                    </template>
                </el-table-column>
                <el-table-column label="操作" width="200" align="center">
                    <template slot-scope="scope">
                        <el-button type="text" size="small" @click="handleViewDetails(scope.row)" class="action-btn" v-if="currentAuditStatus!='unaudited'">
                            详情
                        </el-button>
                        <el-button type="text" size="small" @click="handleAudit(scope.row)" class="action-btn" v-if="currentAuditStatus === 'unaudited'">
                            审核
                        </el-button>
                    </template>
                </el-table-column>
            </el-table>
        </div>

        <!-- 分页 -->
        <div class="pagination-section">
            <el-pagination @size-change="handleSizeChange" @current-change="handleCurrentChange"
                :current-page="pagination.currentPage" :page-sizes="[10, 20, 50, 100]" :page-size="pagination.pageSize"
                layout="total, sizes, prev, pager, next, jumper" :total="pagination.total" class="pagination" />
        </div>

        <!-- 详情弹窗 -->
        <el-dialog title="招标公告详情" :visible.sync="detailDialogVisible" width="75%" :close-on-click-modal="false"
            :append-to-body="true" custom-class="detail-dialog-panel" class="detail-dialog">
            <div class="detail-content" v-if="currentDetail">
                <div class="detail-overview">
                    <div class="overview-main">
                        <div class="overview-title">{{ currentDetail.tenderNoticeName || '-' }}</div>
                        <div class="overview-code">公告编码：{{ currentDetail.tenderNoticeCode || '-' }}</div>
                        <div class="overview-submeta">
                            <div class="overview-stat">
                                <span class="stat-label">涉及标段</span>
                                <strong class="stat-value">{{ getBidSectionList(currentDetail).length }}</strong>
                            </div>
                            <div class="overview-stat">
                                <span class="stat-label">发布时间</span>
                                <strong class="stat-value stat-value--time">{{ currentDetail.noticeSendTime || '-' }}</strong>
                            </div>
                            <div class="overview-stat">
                                <span class="stat-label">截标时间</span>
                                <strong class="stat-value stat-value--time">{{ currentDetail.bidDocumentSubmissionDeadline || '-' }}</strong>
                            </div>
                        </div>
                    </div>
                    <div class="overview-meta">
                        <el-tag size="small" effect="plain">
                            {{ noticeNatureDict[currentDetail.noticeNature] || '-' }}
                        </el-tag>
                    </div>
                </div>

                <div class="detail-panel">
                    <div class="panel-title">公告涉及标段</div>
                    <el-table v-if="getBidSectionList(currentDetail).length" :data="getBidSectionList(currentDetail)" border stripe
                        class="section-table">
                        <el-table-column label="序号" type="index" width="80" align="center"></el-table-column>
                        <el-table-column label="标段编号" prop="bidSectionCode" min-width="220" show-overflow-tooltip></el-table-column>
                        <el-table-column label="标段名称" prop="bidSectionName" min-width="260" show-overflow-tooltip></el-table-column>
                    </el-table>
                    <div v-else class="empty-state">暂无标段信息</div>
                </div>

                <div class="detail-panel">
                    <div class="panel-title">公告信息</div>
                    <div class="detail-grid detail-grid--compact">
                        <div class="detail-field">
                            <div class="field-label">公告性质</div>
                            <div class="field-value">{{ noticeNatureDict[currentDetail.noticeNature] || '-' }}</div>
                        </div>
                        <div class="detail-field">
                            <div class="field-label">公告类型</div>
                            <div class="field-value">{{ noticeTypeDict[currentDetail.noticeType] || '-' }}</div>
                        </div>
                        <div class="detail-field">
                            <div class="field-label">发布状态</div>
                            <div class="field-value">{{ statusDict[currentDetail.status] || '-' }}</div>
                        </div>
                        <div class="detail-field field-span-2">
                            <div class="field-label">原公告编号</div>
                            <div class="field-value is-mono">{{ currentDetail.originalNoticeCode || '-' }}</div>
                        </div>
                    </div>
                </div>

                <div class="detail-panel">
                    <div class="panel-title">时间与流程</div>
                    <div class="detail-grid">
                        <div class="detail-field">
                            <div class="field-label">公告发布时间</div>
                            <div class="field-value is-mono">{{ currentDetail.noticeSendTime || '-' }}</div>
                        </div>
                        <!-- <div class="detail-field">
                            <div class="field-label">公告结束时间</div>
                            <div class="field-value is-mono">{{ currentDetail.noticeEndTime || '-' }}</div>
                        </div> -->
                        <div class="detail-field">
                            <div class="field-label">招标文件获取开始时间</div>
                            <div class="field-value is-mono">{{ currentDetail.biddingDocumentAcquisitionStartTime || '-' }}</div>
                        </div>
                        <div class="detail-field">
                            <div class="field-label">招标文件获取结束时间</div>
                            <div class="field-value is-mono">{{ currentDetail.biddingDocumentAcquisitionEndTime || '-' }}</div>
                        </div>
                        <div class="detail-field">
                            <div class="field-label">招标文件获取范围</div>
                            <div class="field-value">获取期</div>
                        </div>
                        <div class="detail-field field-span-2">
                            <div class="field-label">投标文件递交截止时间</div>
                            <div class="field-value is-mono">{{ currentDetail.bidDocumentSubmissionDeadline || '-' }}</div>
                        </div>
                    </div>
                </div>

                <div class="detail-panel">
                    <div class="panel-title">操作记录</div>
                    <div class="detail-grid">
                        <div class="detail-field">
                            <div class="field-label">创建时间</div>
                            <div class="field-value is-mono">{{ currentDetail.createTime || '-' }}</div>
                        </div>
                        <div class="detail-field">
                            <div class="field-label">审核人</div>
                            <div class="field-value">{{ currentDetail.auditBy || '-' }}</div>
                        </div>
                        <div class="detail-field field-span-2">
                            <div class="field-label">审核时间</div>
                            <div class="field-value is-mono">{{ currentDetail.auditTime || '-' }}</div>
                        </div>
                    </div>
                </div>

                <div class="detail-panel">
                    <div class="panel-title">公告内容</div>
                    <div class="content-box">
                        <div v-html="currentDetail.noticeContent || '-'" class="notice-content"></div>
                    </div>
                </div>

                <div class="detail-panel" v-if="currentDetail.remark">
                    <div class="panel-title">备注</div>
                    <div class="remark-box">{{ currentDetail.remark }}</div>
                </div>

                <div class="detail-panel">
                    <div class="panel-title">审核意见</div>
                    <div class="remark-box">{{ currentDetail.auditOpinion || '-' }}</div>
                </div>

                <!-- 审核区域 -->
                <div class="audit-section" v-if="currentAuditStatus === 'unaudited'">
                    <div class="audit-form">
                        <div class="audit-item">
                            <span class="audit-label">审核结果:</span>
                            <el-radio-group v-model="auditForm.auditResult">
                                <el-radio :label="1">通过</el-radio>
                                <el-radio :label="2">驳回</el-radio>
                            </el-radio-group>
                        </div>
                        <div class="audit-item" v-if="auditForm.auditResult === 2">
                            <span class="audit-label">审核意见:</span>
                            <el-input
                                v-model="auditForm.auditOpinion"
                                type="textarea"
                                :rows="4"
                                placeholder="请输入审核意见"
                                maxlength="500"
                                show-word-limit
                                class="audit-textarea"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div slot="footer" class="dialog-footer">
                <el-button @click="detailDialogVisible = false">关闭</el-button>
                <el-button 
                    type="primary" 
                    @click="handleSubmitAudit" 
                    v-if="currentAuditStatus === 'unaudited'"
                    :disabled="auditForm.auditResult === 2 && !auditForm.auditOpinion.trim()"
                >
                    提交审核
                </el-button>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import { getTenderNoticeAuditList, getTenderNoticeDetailForAudit, checkTenderNotice } from '@/api/agent/api'
export default {
    name: 'AnnouncementAudit',
    data() {
        return {
            loading: false,
            currentAuditStatus: 'unaudited', // 当前审核状态：unaudited-未审核，audited-已审核
            searchForm: {
                announcementName: ''
            },
            // 数据字典
            noticeNatureDict: {
                1: '正常公告',
                2: '变更公告'
            },
            noticeTypeDict: {
                1: '招标公告',
                2: '资格预审公告',
                3: '中标公告',
                9: '其他'
            },
            statusDict: {
                0: '暂存',
                1: '提交',
                2: '退回',
                9: '发布'
            },
            biddingDocumentAcquisitionScopeDict: {
                1: '公告期',
                2: '文件获取期'
            },
            isConfidentialDict: {
                0: '不包含',
                1: '包含'
            },
            tableData: [],
            pagination: {
                currentPage: 1,
                pageSize: 10,
                total: 2
            },
            // 详情弹窗相关
            detailDialogVisible: false,
            currentDetail: null,
            // 审核表单
            auditForm: {
                auditResult: 1, // 1-通过，2-驳回
                auditOpinion: ''
            }
        }
    },
    mounted() {
        this.loadData()
    },
    methods: {
        // 加载数据
        loadData() {
            this.loading = true
            this.getTenderNoticeAuditList()
        },
        // 获取招标公告审核列表
        getTenderNoticeAuditList() {
            const status = this.currentAuditStatus === 'unaudited' ? 1 : 9; // 1-未审核，9-已审核
            const params = {
                status,
                tenderNoticeName: this.searchForm.announcementName,
                pageNum: this.pagination.currentPage,
                pageSize: this.pagination.pageSize
            }
            
            getTenderNoticeAuditList(params).then(res => {
                if (res.code === 200) {
                    this.tableData = res.data.records || []
                    this.pagination.total = res.data.total || 0
                } else {
                    this.$message.error(res.message || '获取数据失败')
                    this.tableData = []
                    this.pagination.total = 0
                }
                this.loading = false
            }).catch(error => {
                console.error('获取数据失败:', error)
                this.$message.error('获取数据失败')
                this.loading = false
            })
        },

        // 切换审核状态
        switchAuditStatus(status) {
            this.currentAuditStatus = status
            this.pagination.currentPage = 1
            this.loadData()
        },

        // 搜索
        handleSearch() {
            this.pagination.currentPage = 1
            this.loadData()
        },

        // 查看详情
        async handleViewDetails(row) {
            try {
                const res = await getTenderNoticeDetailForAudit({ tenderNoticeCode: row.tenderNoticeCode })
                if (res.code === 200) {
                    this.currentDetail = res.data
                    this.detailDialogVisible = true
                    // 重置审核表单
                    this.resetAuditForm()
                } else {
                    this.$message.error('获取公告详情失败')
                }
            } catch (error) {
                console.error('获取公告详情失败:', error)
                this.$message.error('获取公告详情失败')
            }
        },

        // 审核
        handleAudit(row) {
            this.handleViewDetails(row)
        },

        // 查看状态
        handleViewStatus(row) {
            this.handleViewDetails(row)
        },

        // 重置审核表单
        resetAuditForm() {
            this.auditForm = {
                auditResult: 1,
                auditOpinion: ''
            }
        },

        // 提交审核
        async handleSubmitAudit() {
            if (this.auditForm.auditResult === 2 && !this.auditForm.auditOpinion.trim()) {
                this.$message.warning('驳回时必须填写审核意见')
                return
            }

            try {
                const params = {
                    tenderNoticeCode: this.currentDetail.tenderNoticeCode,
                    status: this.auditForm.auditResult === 1 ? 9 : 2, // 1-通过对应9，2-驳回对应2
                    auditOpinion: this.auditForm.auditOpinion
                }

                const res = await checkTenderNotice(params)
                if (res.code === 200) {
                    this.$message.success('审核提交成功')
                    this.detailDialogVisible = false
                    this.loadData() // 重新加载数据
                } else {
                    this.$message.error(res.message || '审核提交失败')
                }
            } catch (error) {
                console.error('审核提交失败:', error)
                this.$message.error('审核提交失败')
            }
        },

        // 分页大小改变
        handleSizeChange(val) {
            this.pagination.pageSize = val
            this.pagination.currentPage = 1
            this.loadData()
        },

        // 当前页改变
        handleCurrentChange(val) {
            this.pagination.currentPage = val
            this.loadData()
        },
        getBidSectionList(detail) {
            if (!detail || !Array.isArray(detail.bidSectionList)) {
                return []
            }
            return detail.bidSectionList
        }
    }
}
</script>

<style lang="less" scoped>
@import '@/styles/agent-child-page-styles.less';

.agent-child-page-styles(announcement-audit);

.page-header {
    display: flex;
    align-items: center;
    margin-bottom: 20px;

    .warning-text {
        margin-left: 10px;
        color: #F56C6C;
        font-size: 14px;
        font-weight: 500;
    }
}

.search-section {
    background: #fff;
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
    margin-bottom: 20px;
    transition: box-shadow 0.3s ease;

    &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    }

    .search-form {
        display: flex;
        align-items: center;
        gap: 16px;
        margin-bottom: 20px;
        flex-wrap: wrap;

        .search-label {
            font-size: 14px;
            color: #606266;
            white-space: nowrap;
            font-weight: 500;
        }

        .search-input {
            width: 240px;
        }

        .search-btn {
            height: 36px;
            padding: 0 24px;
        }
    }

    .audit-status-tabs {
        display: flex;
        justify-content: flex-start;
        
        .tab-buttons {
            border-radius: 6px;
            overflow: hidden;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

            .tab-button {
                padding: 10px 24px;
                border: none;
                background-color: #fff;
                color: #606266;
                font-size: 14px;
                font-weight: 500;
                cursor: pointer;
                transition: all 0.3s ease;
                border-right: 1px solid #e4e7ed;
                outline: none;

                &:first-child {
                    border-top-left-radius: 6px;
                    border-bottom-left-radius: 6px;
                }

                &:last-child {
                    border-top-right-radius: 6px;
                    border-bottom-right-radius: 6px;
                    border-right: none;
                }

                &:hover {
                    background-color: #f5f7fa;
                    transform: translateY(-1px);
                }

                &.active {
                    background-color: #409EFF;
                    color: #fff;
                    border-color: #409EFF;

                    &:hover {
                        background-color: #409EFF;
                    }
                }
            }
        }
    }
}

// 表格样式优化
:deep(.el-table) {
    .el-table__header {
        th {
            background-color: #fff;
            color: #333;
            font-weight: 600;
        }
    }

    .el-table__body {
        tr {
            &:hover {
                background-color: #f5f7fa;
            }
        }
    }
}

// 分页样式优化
:deep(.el-pagination) {
    .el-pagination__total {
        margin-right: 20px;
    }

    .el-pagination__sizes {
        margin-right: 20px;
    }

    .el-pager li {
        &.active {
            background-color: #409EFF;
            color: #fff;
        }
    }

    .el-pagination__jump {
        margin-left: 20px;
    }
}

// 详情弹窗样式
.detail-dialog-panel {
    .el-dialog__body {
        padding: 12px 20px 0;
        background: #ffffff;

        .detail-content {
            padding: 20px;
            background: #ffffff;

            .detail-overview {
                display: flex;
                align-items: flex-start;
                justify-content: space-between;
                gap: 16px;
                padding: 20px;
                margin-bottom: 20px;
                background: #fafafa;
                border-radius: 4px;

                .overview-main {
                    min-width: 0;
                    flex: 1;
                }

                .overview-title {
                    font-size: 22px;
                    font-weight: 600;
                    line-height: 1.5;
                    color: #222;
                    margin-bottom: 12px;
                    word-break: break-word;
                }

                .overview-code {
                    font-size: 14px;
                    color: #8c8c8c;
                    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
                }

                .overview-submeta {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 10px;
                    margin-top: 16px;
                }

                .overview-stat {
                    min-width: 140px;
                    padding: 10px 14px;
                    border-radius: 4px;
                    background: #fff;
                    border: 1px solid #ebeef5;
                    display: flex;
                    flex-direction: column;
                    gap: 6px;

                    .stat-label {
                        font-size: 12px;
                        color: #222;
                        line-height: 1;
                    }

                    .stat-value {
                        font-size: 16px;
                        line-height: 1.4;
                        color: #8c8c8c;
                        font-weight: 600;

                        &.stat-value--time {
                            font-size: 13px;
                            font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
                        }
                    }
                }

                .overview-meta {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    flex-wrap: wrap;
                    justify-content: flex-end;

                    .el-tag {
                        height: 28px;
                        line-height: 26px;
                        padding: 0 12px;
                        border-radius: 4px;
                        border-color: #dcdfe6;
                        background: #fff;
                        color: #606266;
                        font-weight: 500;
                    }
                }
            }

            .detail-panel {
                margin-bottom: 20px;
                padding: 20px;
                background: #fafafa;
                border-radius: 4px;

                .panel-title {
                    margin-bottom: 16px;
                    font-size: 16px;
                    font-weight: 600;
                    color: #222;
                }
            }

            .detail-grid {
                display: grid;
                grid-template-columns: repeat(2, minmax(0, 1fr));
                gap: 0 20px;
            }

            .detail-field {
                padding: 12px 0;
                border-bottom: 1px solid #f0f4f7;
                min-width: 0;
                display: flex;
                align-items: flex-start;

                &.field-span-2 {
                    grid-column: span 2;
                }

                .field-label {
                    min-width: 160px;
                    margin-bottom: 0;
                    padding-right: 12px;
                    font-size: 13px;
                    line-height: 1.7;
                    color: #222;
                    font-weight: 500;
                }

                .field-value {
                    font-size: 14px;
                    line-height: 1.8;
                    color: #8c8c8c;
                    flex: 1;
                    word-break: break-word;

                    &.is-mono {
                        font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
                        font-size: 13px;
                        color: #666;
                    }
                }
            }

            .section-table {
                margin-top: 10px;
            }

            .empty-state {
                padding: 24px;
                border: 1px dashed #e4e7ed;
                border-radius: 4px;
                text-align: center;
                color: #8c8c8c;
                background: #fafafa;
            }

            .content-box {
                border: 1px solid #ebeef5;
                border-radius: 4px;
                background: #fff;
                overflow: hidden;
            }

            .notice-content {
                max-height: 420px;
                overflow-y: auto;
                padding: 20px;
                line-height: 1.9;
                color: #8c8c8c;
                word-break: break-word;
            }

            .remark-box {
                padding: 16px 18px;
                background: #fff;
                border: 1px solid #ebeef5;
                border-radius: 4px;
                line-height: 1.8;
                color: #8c8c8c;
                white-space: pre-wrap;
            }
        }
    }

    .audit-section {
        margin-top: 20px;
        padding: 20px;
        background-color: #f8f9fa;
        border-radius: 8px;
        border: 1px solid #e9ecef;

        .audit-form {
            .audit-item {
                display: flex;
                align-items: flex-start;
                margin-bottom: 20px;

                .audit-label {
                    color: #666;
                    font-weight: 500;
                    min-width: 100px;
                    flex-shrink: 0;
                    margin-right: 15px;
                }

                .audit-textarea {
                    flex: 1;
                    max-width: 400px;
                }
            }
        }
    }
}

.dialog-footer {
    text-align: center;
    padding: 18px 0 8px;
    border-top: 1px solid #ebeef5;
    margin-top: 6px;
}
</style>